'use client';

import { useState } from 'react';

import { Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  type AppliedDiscount,
  DiscountCodeInput,
  type SuggestedDiscountCode,
} from '@kit/booking-addons/components/discount-code-input';
import { currencyMinorUnits, formatCurrency } from '@kit/shared/currency';
import { Button } from '@kit/ui/button';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';

import { canPayMembershipWithWallet } from '../_lib/membership-wallet';
import type { AvailableMembershipPlan } from '../_lib/server/available-memberships.loader';

function Money({ value }: { value: number }) {
  const { i18n } = useTranslation();
  const currency = useTenantCurrency();
  const digits = currencyMinorUnits(currency);

  return (
    <span className="inline-flex items-baseline gap-1 tabular-nums">
      <CurrencyIcon className="h-3.5 w-3.5 self-center" />
      {formatCurrency(value, currency, i18n.language, {
        style: 'decimal',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}
    </span>
  );
}

export interface ReviewSelection {
  discountCode: string | undefined;
  /** The previewed charge after the code; the server prices it again. */
  total: number;
}

/**
 * The step between choosing a self-serve plan and paying: the buyer can
 * enter a discount code and sees what they will be charged. The discount is
 * only a preview here — checkout prices the code again on the server.
 */
export function MembershipCheckoutReview({
  plan,
  tenantId,
  wallet,
  suggestedCodes,
  pending,
  onClose,
  onPay,
  onPayWithWallet,
}: {
  plan: AvailableMembershipPlan;
  tenantId: string;
  wallet: { enabled: boolean; balance: number };
  suggestedCodes: SuggestedDiscountCode[];
  pending: boolean;
  onClose: () => void;
  onPay: (selection: ReviewSelection) => void;
  onPayWithWallet: (selection: ReviewSelection) => void;
}) {
  const { t } = useTranslation();
  const [applied, setApplied] = useState<AppliedDiscount | null>(null);

  const price = Number(plan.price);
  const discount = applied ? Math.min(applied.amountOff, price) : 0;
  const total = Math.round((price - discount) * 100) / 100;
  const selection: ReviewSelection = { discountCode: applied?.code, total };
  const canUseWallet = canPayMembershipWithWallet({
    isAuthenticated: true,
    isAuthorizedPayer: true,
    walletEnabled: wallet.enabled,
    walletBalance: wallet.balance,
    amount: total,
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
    >
      <DialogContent data-test="membership-checkout-review">
        <DialogHeader>
          <DialogTitle>
            {t('common:memberClub.memberships.purchase.review.title', {
              plan: plan.name,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('common:memberClub.memberships.purchase.review.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <DiscountCodeInput
            mode={{
              kind: 'preview',
              accountId: tenantId,
              draftTotal: price,
              membershipPlanId: plan.id,
            }}
            applied={applied}
            onApplied={setApplied}
            onRemoved={() => setApplied(null)}
            showCancelNote={false}
            suggestedCodes={suggestedCodes}
            autoApplySingle
          />

          <dl className="space-y-1 rounded-lg border px-3 py-2 text-sm">
            {discount > 0 && (
              <>
                <div className="text-muted-foreground flex items-center justify-between">
                  <dt>{t('common:memberClub.payment.fieldSubtotal')}</dt>
                  <dd>
                    <Money value={price} />
                  </dd>
                </div>
                <div className="text-success-foreground flex items-center justify-between">
                  <dt>
                    {t(
                      'common:memberClub.memberships.purchase.review.discount',
                      {
                        code: selection.discountCode,
                      },
                    )}
                  </dt>
                  <dd dir="ltr">
                    −<Money value={discount} />
                  </dd>
                </div>
              </>
            )}
            <div className="flex items-center justify-between font-semibold">
              <dt>
                {t('common:memberClub.memberships.purchase.review.total')}
              </dt>
              <dd>
                <Money value={total} />
              </dd>
            </div>
          </dl>

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              size="lg"
              className="w-full"
              disabled={pending}
              onClick={() => onPay(selection)}
              data-test="membership-checkout-pay"
            >
              {total > 0
                ? t('common:memberClub.memberships.purchase.review.pay')
                : t('common:memberClub.memberships.purchase.review.joinFree')}
            </Button>
            {canUseWallet && (
              <Button
                type="button"
                size="lg"
                variant="outline"
                className="w-full"
                disabled={pending}
                onClick={() => onPayWithWallet(selection)}
                data-test="membership-checkout-pay-wallet"
              >
                <Wallet className="me-2 h-4 w-4" aria-hidden />
                {t('common:memberClub.memberships.purchase.payWithWallet')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
